import React, { useMemo, useState, useEffect, useRef } from 'react';
import { FaCrown } from 'react-icons/fa';

const FONT = "'DM Sans', 'Segoe UI', sans-serif";
const THEME = 'var(--activemenu, #3d91e4)';
const THEME_SOFT = 'color-mix(in srgb, var(--activemenu, #3d91e4) 14%, #ffffff)';
const NAVY = '#0f172a';
const MUTED = '#94a3b8';
const AMBER = '#d97706';
const RED = '#dc2626';

/** Color for remaining-credit %: healthy → warn → critical (theme-aware) */
function creditTone(remainPct) {
  const p = Number(remainPct) || 0;
  if (p <= 15) {
    return {
      solid: RED,
      soft: 'rgba(255,255,255,0.22)',
      label: 'critical',
      gradient: 'linear-gradient(105deg, #ef4444 0%, #be123c 100%)',
    };
  }
  if (p <= 40) {
    return {
      solid: AMBER,
      soft: 'rgba(255,255,255,0.22)',
      label: 'warn',
      gradient: 'linear-gradient(105deg, #f59e0b 0%, #d97706 100%)',
    };
  }
  return {
    solid: THEME,
    soft: 'rgba(255,255,255,0.22)',
    label: 'ok',
    gradient:
      'linear-gradient(105deg, var(--activemenu, #3d91e4) 0%, color-mix(in srgb, var(--activemenu, #3d91e4) 45%, #7c3aed) 100%)',
  };
}

function PillRing({ pct, size = 22, stroke = 2.2, track = 'rgba(255,255,255,0.28)', color = '#ffffff' }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const value = Math.min(100, Math.max(0, pct));
  const offset = c - (value / 100) * c;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)', display: 'block', flexShrink: 0 }} aria-hidden="true">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        style={{ transition: 'stroke-dashoffset 0.45s ease' }}
      />
    </svg>
  );
}

function clampPct(used, total) {
  const u = Number(used) || 0;
  const t = Number(total) || 0;
  if (t <= 0) return 0;
  return Math.min(100, Math.max(0, (u / t) * 100));
}

function formatDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(`${value}T00:00:00`);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return String(value);
  }
}

function formatNum(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  if (Number.isInteger(n)) return n.toLocaleString();
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function urgency(expired, daysLeft) {
  if (expired) return 'critical';
  if (daysLeft <= 3) return 'critical';
  if (daysLeft <= 7) return 'warn';
  return 'ok';
}

function TargetIcon({ size = 18, color = '#fff' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="7.25" stroke={color} strokeWidth="1.6" />
      <circle cx="10" cy="10" r="4.25" stroke={color} strokeWidth="1.6" />
      <circle cx="10" cy="10" r="1.5" fill={color} />
    </svg>
  );
}

function CreditIcon({ size = 18, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="8" cy="10" r="4" stroke={color} strokeWidth="1.35" />
      <circle cx="13" cy="10" r="4" stroke={color} strokeWidth="1.35" />
      <path
        d="M5.8 11.8a5.2 5.2 0 0 0 4.2 2.2 5.2 5.2 0 0 0 4.2-2.2M5.8 8.2a5.2 5.2 0 0 1 4.2-2.2 5.2 5.2 0 0 1 4.2 2.2"
        stroke={color}
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PercentRing({ pct, size = 88, stroke = 9, color = THEME }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const value = Math.min(100, Math.max(0, pct));
  const offset = c - (value / 100) * c;

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.5s cubic-bezier(0.22,1,0.36,1), stroke 0.35s ease' }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 18,
          fontWeight: 750,
          color,
          letterSpacing: '-0.03em',
          fontVariantNumeric: 'tabular-nums',
          transition: 'color 0.35s ease',
        }}
      >
        {Math.round(value)}%
      </div>
    </div>
  );
}

function ProgressBar({ pct, color = THEME, height = 6 }) {
  const value = Math.min(100, Math.max(0, Number(pct) || 0));
  return (
    <div
      style={{
        width: '100%',
        height,
        borderRadius: 999,
        background: '#eef2f7',
        overflow: 'hidden',
      }}
      aria-hidden="true"
    >
      <div
        style={{
          width: `${value}%`,
          height: '100%',
          borderRadius: 999,
          background: color,
          transition: 'width 0.45s cubic-bezier(0.22,1,0.36,1), background 0.3s ease',
        }}
      />
    </div>
  );
}

function LimitRow({ label, used, max }) {
  const usedN = Number(used) || 0;
  const maxN = Number(max) || 0;
  const left = Math.max(0, maxN - usedN);
  const usedPct = maxN > 0 ? clampPct(usedN, maxN) : 0;
  const depleted = maxN > 0 && left <= 0;
  const low = maxN > 0 && left / maxN <= 0.15;
  const barColor = depleted || low ? RED : usedPct >= 60 ? AMBER : THEME;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: '#334155' }}>{label}</span>
        <span style={{ fontSize: 12.5, fontWeight: 650, color: depleted ? RED : NAVY, fontVariantNumeric: 'tabular-nums' }}>
          {formatNum(usedN)}
          <span style={{ fontWeight: 500, color: MUTED }}> / {formatNum(maxN)}</span>
        </span>
      </div>
      <ProgressBar pct={usedPct} color={barColor} height={5} />
      <div style={{ fontSize: 11, color: depleted ? RED : MUTED }}>
        {depleted
          ? `Limit reached · ${formatNum(usedN)} of ${formatNum(maxN)} used`
          : `${formatNum(left)} left · ${formatNum(usedN)} of ${formatNum(maxN)} used`}
      </div>
    </div>
  );
}

/** Detail card — trial / credit usage popup */
export default function TrialUsageSummary({ trial, onViewUsage, embedded = false }) {
  const stats = useMemo(() => {
    if (!trial) return null;
    const totalCredits = Number(trial.totalCredits) || 0;
    const usedCredits = Number(trial.usedCredits) || 0;
    const remainingCredits = Math.max(0, totalCredits - usedCredits);
    const daysLeft = Number(trial.daysLeft) || 0;
    return {
      totalCredits,
      usedCredits,
      remainingCredits,
      remainPct: totalCredits ? clampPct(remainingCredits, totalCredits) : 0,
      daysLeft,
      expired: Boolean(trial.trialExpired),
      projectsUsed: Number(trial.projectCount) || 0,
      projectsMax: Number(trial.maxProjects) || 0,
      projectsLeft: Math.max(0, (Number(trial.maxProjects) || 0) - (Number(trial.projectCount) || 0)),
      runsUsed: Number(trial.agentExecutionCount) || 0,
      runsMax: Number(trial.maxAgentExecutions) || 0,
      runsLeft: Math.max(0, (Number(trial.maxAgentExecutions) || 0) - (Number(trial.agentExecutionCount) || 0)),
      end: trial.trialEndDate,
    };
  }, [trial]);

  if (!stats) return null;

  const level = urgency(stats.expired, stats.daysLeft);
  const tone = creditTone(stats.remainPct);
  const statusLabel = stats.expired ? 'Ended' : `${stats.daysLeft}d left`;
  const statusBg = level === 'critical' ? '#fef2f2' : level === 'warn' ? '#fffbeb' : THEME_SOFT;
  const statusFg = level === 'critical' ? RED : level === 'warn' ? AMBER : THEME;
  const hint =
    stats.expired
      ? 'Your trial has ended. Upgrade to keep building.'
      : stats.remainPct <= 15
        ? 'Credits are running low. Review usage or upgrade soon.'
        : stats.projectsLeft <= 0
          ? `You've used all ${formatNum(stats.projectsMax)} project${stats.projectsMax === 1 ? '' : 's'} on this trial.`
          : null;

  return (
    <div
      style={{
        width: '100%',
        borderRadius: embedded ? 0 : 16,
        overflow: 'hidden',
        fontFamily: FONT,
        background: '#ffffff',
        border: embedded ? 'none' : '1px solid rgba(15,23,42,0.08)',
        boxShadow: embedded ? 'none' : '0 18px 40px rgba(15,23,42,0.12), 0 2px 8px rgba(15,23,42,0.04)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '14px 16px 12px',
          borderBottom: '1px solid rgba(15,23,42,0.06)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: THEME_SOFT,
              color: THEME,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <TargetIcon size={13} color="currentColor" />
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: NAVY, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
              Trial usage
            </div>
            <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
              Ends {formatDate(stats.end)}
            </div>
          </div>
        </div>
        <span
          style={{
            height: 24,
            padding: '0 9px',
            borderRadius: 999,
            display: 'inline-flex',
            alignItems: 'center',
            background: statusBg,
            color: statusFg,
            fontSize: 11,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {statusLabel}
        </span>
      </div>

      {/* Primary metric: credits */}
      <div style={{ padding: '16px 16px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <PercentRing pct={stats.remainPct} color={tone.solid} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11.5, fontWeight: 600, color: MUTED, marginBottom: 6 }}>
              Credits left
            </div>
            <div
              style={{
                fontSize: 34,
                fontWeight: 750,
                color: tone.solid,
                letterSpacing: '-0.05em',
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1,
                transition: 'color 0.35s ease',
              }}
            >
              {formatNum(stats.remainingCredits)}
            </div>
            <div style={{ marginTop: 8, fontSize: 12.5, color: '#64748b' }}>
              {formatNum(stats.usedCredits)} used of {formatNum(stats.totalCredits)}
            </div>
          </div>
        </div>

        {hint ? (
          <div
            style={{
              marginTop: 12,
              padding: '9px 11px',
              borderRadius: 10,
              background: level === 'critical' || stats.remainPct <= 15 ? '#fef2f2' : '#fffbeb',
              color: level === 'critical' || stats.remainPct <= 15 ? RED : AMBER,
              fontSize: 12,
              fontWeight: 550,
              lineHeight: 1.35,
            }}
          >
            {hint}
          </div>
        ) : null}
      </div>

      {/* Limits */}
      <div
        style={{
          margin: '0 16px',
          padding: '14px 0 4px',
          borderTop: '1px solid rgba(15,23,42,0.06)',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <LimitRow
          label="Projects"
          used={stats.projectsUsed}
          max={stats.projectsMax}
        />
        <LimitRow
          label="Agent runs"
          used={stats.runsUsed}
          max={stats.runsMax}
        />
      </div>
    </div>
  );
}

/**
 * AWS-style top page alert when trial credits are exhausted or trial ended.
 */
export function TrialCreditAlert({ trial, onUpgrade, onDismiss }) {
  if (!trial) return null;

  const totalCredits = Number(trial.totalCredits) || 0;
  const usedCredits = Number(trial.usedCredits) || 0;
  const remainingCredits = Math.max(0, totalCredits - usedCredits);
  const expired = Boolean(trial.trialExpired);
  const depleted = totalCredits > 0 && remainingCredits <= 0;

  if (!expired && !depleted) return null;

  const title = expired ? 'Trial ended' : 'Credits exhausted';
  const body = expired
    ? 'Your free trial has ended. Upgrade your plan to keep building and running apps.'
    : 'You have used all available trial credits. Upgrade your plan to continue building and running apps.';

  return (
    <div
      role="alert"
      style={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        margin: '12px 16px 0',
        padding: '12px 14px',
        borderRadius: 8,
        border: '1px solid #f5c2c7',
        borderLeft: '4px solid #dc3545',
        background: '#fdf3f4',
        fontFamily: FONT,
        boxShadow: 'none',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: '#dc3545',
          color: '#fff',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginTop: 1,
        }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M6 3.2v3.4M6 8.5h.01" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          <circle cx="6" cy="6" r="4.6" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 700,
            color: '#8b1e26',
            letterSpacing: '-0.01em',
            lineHeight: 1.3,
            marginBottom: 3,
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 13, color: '#842029', lineHeight: 1.45 }}>
          {body}{' '}
          {onUpgrade ? (
            <button
              type="button"
              onClick={onUpgrade}
              style={{
                border: 'none',
                background: 'none',
                padding: 0,
                color: '#0b5ed7',
                fontWeight: 650,
                fontSize: 13,
                cursor: 'pointer',
                fontFamily: FONT,
                textDecoration: 'underline',
                textUnderlineOffset: 2,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                verticalAlign: 'baseline',
              }}
            >
              <FaCrown size={14} color="#d97706" aria-hidden="true" style={{ flexShrink: 0 }} />
              Upgrade to Premium
            </button>
          ) : null}
        </div>
      </div>

      {onDismiss ? (
        <button
          type="button"
          aria-label="Dismiss alert"
          onClick={onDismiss}
          style={{
            width: 28,
            height: 28,
            border: '1px solid #f5c2c7',
            borderRadius: 6,
            background: '#fff',
            color: '#842029',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            padding: 0,
          }}
        >
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M2.5 2.5l7 7M9.5 2.5l-7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      ) : null}
    </div>
  );
}

/** Compact chip + dropdown — use variant="sidebar" inside the hub sidebar */
export function TrialUsageBanner({ trial, onViewUsage, variant = 'floating', compact = false }) {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState(null);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const isSidebar = variant === 'sidebar';

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !isSidebar || !btnRef.current) {
      setPanelPos(null);
      return undefined;
    }
    const update = () => {
      const r = btnRef.current.getBoundingClientRect();
      setPanelPos({
        left: Math.min(r.left, window.innerWidth - 316),
        bottom: window.innerHeight - r.top + 10,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, isSidebar]);

  if (!trial) return null;

  const expired = Boolean(trial.trialExpired);
  const daysLeft = Number(trial.daysLeft) || 0;
  const totalCredits = Number(trial.totalCredits) || 0;
  const remainingCredits = Math.max(0, totalCredits - (Number(trial.usedCredits) || 0));
  const remainPct = totalCredits ? clampPct(remainingCredits, totalCredits) : 0;
  const tone = creditTone(remainPct);
  const statusLabel = expired ? 'Ended' : `${daysLeft}d left`;

  const panel = open ? (
    <div
      className="tu-panel"
      role="dialog"
      aria-label="Credit details"
      style={
        isSidebar && panelPos
          ? {
              position: 'fixed',
              left: panelPos.left,
              bottom: panelPos.bottom,
              width: 300,
              zIndex: 1600,
            }
          : {
              position: 'absolute',
              top: 'calc(100% + 10px)',
              right: 0,
              width: 300,
              zIndex: 50,
            }
      }
      onClick={(e) => e.stopPropagation()}
    >
      <TrialUsageSummary
        trial={trial}
        onViewUsage={
          onViewUsage
            ? () => {
                setOpen(false);
                onViewUsage();
              }
            : undefined
        }
      />
    </div>
  ) : null;

  return (
    <>
      <style>{`
        @keyframes tuPillIn {
          from { opacity: 0; transform: translateY(6px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes tuPanel {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .tu-credit-pill {
          animation: tuPillIn 0.22s ease;
          transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, filter 0.2s ease, transform 0.15s ease;
        }
        .tu-credit-pill--float:hover,
        .tu-credit-pill--float.is-open {
          transform: translateY(-1px);
          filter: brightness(1.05);
          box-shadow: 0 10px 24px rgba(15,23,42,0.28) !important;
        }
        .tu-credit-pill--rail:hover {
          background: #f8fafc !important;
        }
        .tu-credit-pill--sidebar:hover,
        .tu-credit-pill--sidebar.is-open {
          background: #f8fafc !important;
          border-color: rgba(148, 163, 184, 0.45) !important;
        }
        .tu-panel { animation: tuPanel 0.18s ease; }
      `}</style>

      <div
        ref={wrapRef}
        style={{
          position: 'relative',
          display: isSidebar ? 'flex' : 'inline-flex',
          width: isSidebar ? '100%' : undefined,
          flexDirection: 'column',
          alignItems: isSidebar ? (compact ? 'center' : 'stretch') : 'flex-end',
          fontFamily: FONT,
          zIndex: 1200,
        }}
      >
        <button
          ref={btnRef}
          type="button"
          className={`tu-credit-pill${
            compact ? ' tu-credit-pill--rail' : isSidebar ? ' tu-credit-pill--sidebar' : ' tu-credit-pill--float'
          }${open ? ' is-open' : ''}`}
          aria-expanded={open}
          aria-haspopup="dialog"
          title={`${formatNum(remainingCredits)} credits · ${statusLabel}`}
          aria-label={`${formatNum(remainingCredits)} credits remaining, ${statusLabel}`}
          onClick={() => setOpen((v) => !v)}
          style={
            compact
              ? {
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  padding: 0,
                  borderRadius: 8,
                  border: 'none',
                  background: open
                    ? 'color-mix(in srgb, var(--activemenu, #3d91e4) 11%, #ffffff)'
                    : 'transparent',
                  cursor: 'pointer',
                  fontFamily: FONT,
                  boxShadow: 'none',
                }
              : isSidebar
                ? {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    height: 42,
                    padding: '0 10px',
                    borderRadius: 10,
                    border: '1px solid rgba(148, 163, 184, 0.28)',
                    background: open ? '#f8fafc' : '#ffffff',
                    color: '#0f172a',
                    cursor: 'pointer',
                    fontFamily: FONT,
                    whiteSpace: 'nowrap',
                    boxShadow: 'none',
                  }
                : {
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 8,
                    height: 34,
                    padding: '0 6px 0 7px',
                    borderRadius: 999,
                    border: '1px solid rgba(255,255,255,0.28)',
                    background: tone.gradient,
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontFamily: FONT,
                    whiteSpace: 'nowrap',
                    boxShadow: '0 8px 20px rgba(15,23,42,0.22)',
                  }
          }
        >
          {compact ? (
            <span
              style={{
                width: 20,
                height: 20,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: open || tone.label !== 'ok' ? tone.solid : '#64748b',
                transition: 'color 0.15s ease',
              }}
            >
              <CreditIcon size={18} color="currentColor" />
            </span>
          ) : isSidebar ? (
            <>
              <span style={{ position: 'relative', width: 26, height: 26, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <PillRing
                  pct={remainPct}
                  size={26}
                  stroke={2.2}
                  track="rgba(148,163,184,0.35)"
                  color={tone.solid}
                />
                <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <TargetIcon size={10} color={tone.solid} />
                </span>
              </span>

              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, minWidth: 0, flex: 1 }}>
                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4, lineHeight: 1.1 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', color: '#0f172a' }}>
                    {formatNum(remainingCredits)}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 500, color: '#64748b' }}>credits</span>
                </span>
                <span style={{ fontSize: 10.5, fontWeight: 500, color: tone.solid, lineHeight: 1.1 }}>
                  {statusLabel}
                </span>
              </span>

              <span
                aria-hidden="true"
                style={{
                  color: '#94a3b8',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transform: open ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.18s ease',
                }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 7.5L6 4l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </>
          ) : (
            <>
              <span style={{ position: 'relative', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <PillRing pct={remainPct} />
                <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <TargetIcon size={9} color="#fff" />
                </span>
              </span>

              <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4, lineHeight: 1, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 750, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
                  {formatNum(remainingCredits)}
                </span>
                <span style={{ fontSize: 11, fontWeight: 500, opacity: 0.92 }}>credits</span>
              </span>

              <span aria-hidden="true" style={{ width: 1, height: 14, background: 'rgba(255,255,255,0.35)', flexShrink: 0, marginLeft: 'auto' }} />

              <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.95, flexShrink: 0 }}>
                {statusLabel}
              </span>

              <span
                aria-hidden="true"
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: 'rgba(0,0,0,0.18)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transform: open ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.18s ease',
                }}
              >
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 7.5L6 4l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </>
          )}
        </button>

        {panel}
      </div>
    </>
  );
}
